import { useEffect, useRef, useState } from "react";
import { Globe, Phone, TrendingUp, Mail, BarChart3, Users } from "lucide-react";
import hansPhoto from "@/assets/hans-photo.webp";
import shawnPhoto from "@/assets/shawn-photo.webp";
import teamOfficeFull from "@/assets/team-office-full.webp";

const stats = [
  { icon: Globe, value: "650+", label: "Sites Launched" },
  { icon: Phone, value: "60.6K", label: "Calls Generated" },
  { icon: BarChart3, value: "$2.53M", label: "Ad Spend Managed" },
  { icon: TrendingUp, value: "6,530", label: "SEO Pages" },
  { icon: Mail, value: "26.2M", label: "Mailers Sent" },
  { icon: Users, value: "95%", label: "Retention" },
];

const CheckupMeetXpress = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="who-we-are" className="py-12 md:py-16 bg-secondary" ref={ref}>
      <div className="max-w-5xl mx-auto section-padding">
        {/* Header */}
        <div className="text-center mb-8">
          <p className="text-accent font-semibold text-xs tracking-wide uppercase mb-1.5">
            Who's Behind the Checkup
          </p>
          <h2 className="text-xl md:text-2xl font-bold text-foreground tracking-tight mb-2">
            Built by Dental Marketing Veterans — Not a Generic Agency
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-sm leading-relaxed">
            Founded in 2005 by two partners who grew their own family dental practice from $1M to $12M — then built an agency to do the same for hundreds of dentists nationwide.
          </p>
        </div>

        {/* Team photo with stats overlay */}
        <div className={`relative rounded-xl overflow-hidden mb-6 ${visible ? "reveal-up" : "opacity-0"}`}>
          {/* Background team photo */}
          <img
            src={teamOfficeFull}
            alt="The Xpress team at HQ in Springfield, VA"
            className="w-full aspect-[21/9] object-cover object-top"
            loading="lazy"
          />
          {/* Dark gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-black/20" />

          {/* Stats overlaid on the photo */}
          <div className="absolute inset-x-0 bottom-0 p-4 md:p-6">
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3 md:gap-4">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <stat.icon className="w-3.5 h-3.5 text-accent mx-auto mb-1 drop-shadow-md" />
                  <p className="text-base md:text-lg font-bold text-white tracking-tight drop-shadow-md">
                    {stat.value}
                  </p>
                  <p className="text-[9px] md:text-[10px] text-white/70 font-medium leading-tight">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Founders — compact inline */}
        <div className={`grid grid-cols-2 gap-4 ${visible ? "reveal-up" : "opacity-0"}`} style={{ animationDelay: "120ms" }}>
          {[
            { name: "Hans Hillestad", role: "CEO & Co-Founder", img: hansPhoto, note: "20 years of dental marketing operations & strategy" },
            { name: "Shawn Kimmel", role: "COO & Co-Founder", img: shawnPhoto, note: "Grew a family practice from $1M → $12M revenue" },
          ].map((f) => (
            <div key={f.name} className="flex items-center gap-3 bg-card border border-border rounded-lg p-3 shadow-sm">
              <img
                src={f.img}
                alt={f.name}
                className="w-14 h-14 rounded-full object-cover object-top flex-shrink-0 border-2 border-accent/20"
                loading="lazy"
              />
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-foreground leading-tight">{f.name}</h3>
                <p className="text-[11px] font-semibold text-accent">{f.role}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{f.note}</p>
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-[10px] text-muted-foreground mt-5">
          In-house team · Springfield, VA · Serving dental practices nationwide since 2005
        </p>
      </div>
    </section>
  );
};

export default CheckupMeetXpress;
